/**
 * Hook para gestionar bloques del editor de blog.
 */

import { useState, useCallback } from 'react';
import type { BlogBlock } from './types';
import { htmlToBlocks } from './html-to-blocks';
import { blocksToHtml } from './blocks-to-html';

export function useBlogBlockEditor(initialHtml: string) {
    const [blocks, setBlocks] = useState<BlogBlock[]>(() =>
        htmlToBlocks(initialHtml || '')
    );

    const syncFromHtml = useCallback((html: string) => {
        setBlocks(htmlToBlocks(html || ''));
    }, []);

    const toHtml = useCallback((): string => {
        return blocksToHtml(blocks);
    }, [blocks]);

    const updateBlock = useCallback((id: string, updates: Partial<BlogBlock>) => {
        setBlocks((prev) =>
            prev.map((b) => (b.id === id ? ({ ...b, ...updates } as BlogBlock) : b))
        );
    }, []);

    const moveBlock = useCallback((id: string, direction: 'up' | 'down') => {
        setBlocks((prev) => {
            const sorted = [...prev].sort((a, b) => a.order - b.order);
            const idx = sorted.findIndex((b) => b.id === id);
            if (idx < 0) return prev;
            const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
            if (swapIdx < 0 || swapIdx >= sorted.length) return prev;
            const orderA = sorted[idx].order;
            const orderB = sorted[swapIdx].order;
            return prev.map((b) => {
                if (b.id === sorted[idx].id)
                    return { ...b, order: orderB } as BlogBlock;
                if (b.id === sorted[swapIdx].id)
                    return { ...b, order: orderA } as BlogBlock;
                return b;
            });
        });
    }, []);

    const removeBlock = useCallback((id: string) => {
        setBlocks((prev) => {
            const filtered = prev.filter((b) => b.id !== id);
            return filtered.map((b, i) => ({ ...b, order: i } as BlogBlock));
        });
    }, []);

    const duplicateBlock = useCallback((id: string) => {
        setBlocks((prev) => {
            const source = prev.find((b) => b.id === id);
            if (!source) return prev;
            const newBlock = {
                ...source,
                id: crypto.randomUUID(),
                order: source.order + 0.5,
            } as BlogBlock;
            const withNew = [...prev, newBlock];
            const sorted = withNew.sort((a, b) => a.order - b.order);
            return sorted.map((b, i) => ({ ...b, order: i } as BlogBlock));
        });
    }, []);

    const addBlock = useCallback(
        (
            type: BlogBlock['type'],
            options?: { afterId?: string; overrides?: Partial<BlogBlock> }
        ) => {
            const defaults: Record<BlogBlock['type'], Partial<BlogBlock>> = {
                heading: { type: 'heading', level: 2, content: '' },
                paragraph: { type: 'paragraph', content: '' },
                image: { type: 'image', url: '', alt: '' },
                list: { type: 'list', listType: 'bullet', items: [''] },
                blockquote: { type: 'blockquote', content: '' },
            };
            const afterId = typeof options === 'string' ? options : options?.afterId;
            const overrides = typeof options === 'object' ? options?.overrides : undefined;
            const base = { ...defaults[type], ...overrides } as Omit<BlogBlock, 'id' | 'order'>;
            setBlocks((prev) => {
                const sorted = [...prev].sort((a, b) => a.order - b.order);
                let insertOrder: number;
                if (afterId) {
                    const idx = sorted.findIndex((b) => b.id === afterId);
                    insertOrder = idx >= 0 ? sorted[idx].order + 0.5 : prev.length;
                } else {
                    insertOrder = prev.length;
                }
                const newBlock = {
                    ...base,
                    id: crypto.randomUUID(),
                    order: insertOrder,
                } as BlogBlock;
                const withNew = [...prev, newBlock];
                const reordered = withNew
                    .sort((a, b) => a.order - b.order)
                    .map((b, i) => ({ ...b, order: i } as BlogBlock));
                return reordered;
            });
        },
        []
    );

    return {
        blocks,
        setBlocks,
        syncFromHtml,
        toHtml,
        updateBlock,
        moveBlock,
        removeBlock,
        duplicateBlock,
        addBlock,
    };
}
